export interface HoverIntentOptions {
  /** Max cursor movement (px) to consider hover intentional. Default: `7` */
  sensitivity: number
  /** Re-check interval in ms. Default: `100` */
  interval: number
  /** Delay in ms before mouseout fires. Default: `0` */
  timeout: number
  /** Also trigger on keyboard focus/blur. Default: `false` */
  handleFocus: boolean
}

export interface HoverIntentHandle {
  /** Update options. Returns the handle for chaining. */
  options: (opts: Partial<HoverIntentOptions>) => HoverIntentHandle
  /** Remove all event listeners from the element. */
  remove: () => void
}

export type HoverIntentCallback = (e: MouseEvent | FocusEvent) => void

const enum HoverState {
  Idle,
  Active,
}

const PASSIVE: AddEventListenerOptions = { passive: true }

const DEFAULT_OPTIONS = {
  sensitivity: 7,
  interval: 100,
  timeout: 0,
  handleFocus: false,
} as const satisfies HoverIntentOptions

/**
 * Tracks cursor movement on a DOM element and distinguishes intentional hovers
 * from accidental pass-throughs.
 *
 * The algorithm periodically compares the cursor's current position against
 * its position at the previous check. If total movement falls below
 * `sensitivity`, the hover is considered intentional and `onOver` fires.
 */
class HoverIntentTracker {
  private cursorX = 0
  private cursorY = 0
  private previousX = 0
  private previousY = 0

  private isMouseOver = false
  private isFocused = false
  private state: HoverState = HoverState.Idle
  private timer: ReturnType<typeof setTimeout> | null = null
  private config: HoverIntentOptions

  constructor(
    private readonly el: HTMLElement,
    private readonly onOver: HoverIntentCallback,
    private readonly onOut: HoverIntentCallback,
  ) {
    this.config = { ...DEFAULT_OPTIONS }
    this.bindMouseListeners()
  }

  private clearTimer(): void {
    if (this.timer === null) return
    clearTimeout(this.timer)
    this.timer = null
  }

  private startTimer(callback: () => void, delay: number): void {
    this.clearTimer()
    this.timer = setTimeout(callback, delay)
  }

  private readonly handleMouseMove = (e: MouseEvent): void => {
    this.cursorX = e.clientX
    this.cursorY = e.clientY
  }

  /**
   * Compares current cursor position against the previous snapshot.
   * If total movement is below `sensitivity`, the hover is intentional.
   * Otherwise, snapshots the position and schedules another check.
   *
   * Note: `e` is the original `mouseover` event captured when detection began,
   * so its `clientX/clientY` reflect the entry point, not the cursor's position
   * when `onOver` finally fires. This mirrors the original `hoverintent`
   * library's behavior and is preserved intentionally for backward parity —
   * live coordinates are tracked separately via `cursorX/cursorY`.
   */
  private readonly evaluateIntent = (e: MouseEvent): void => {
    this.clearTimer()

    const totalMovement = Math.abs(this.previousX - this.cursorX) + Math.abs(this.previousY - this.cursorY)

    if (totalMovement < this.config.sensitivity) {
      this.state = HoverState.Active

      // Intent resolved — stop tracking movement. `handleMouseOver` re-adds the
      // listener on the next Idle entry, so leaving it bound here would only
      // burn cycles writing cursorX/cursorY with no consumer.
      this.el.removeEventListener('mousemove', this.handleMouseMove)

      if (!this.isFocused) {
        this.onOver(e)
      }
      return
    }

    this.previousX = this.cursorX
    this.previousY = this.cursorY
    this.startTimer(() => this.evaluateIntent(e), this.config.interval)
  }

  private readonly handleMouseOver = (e: MouseEvent): void => {
    this.isMouseOver = true
    this.clearTimer()
    this.el.removeEventListener('mousemove', this.handleMouseMove)

    if (this.state === HoverState.Active) return

    this.previousX = e.clientX
    this.previousY = e.clientY
    this.el.addEventListener('mousemove', this.handleMouseMove, PASSIVE)

    this.startTimer(() => this.evaluateIntent(e), this.config.interval)
  }

  private readonly handleMouseOut = (e: MouseEvent): void => {
    this.isMouseOver = false
    this.clearTimer()
    this.el.removeEventListener('mousemove', this.handleMouseMove)

    if (this.state !== HoverState.Active) return

    this.startTimer(() => {
      this.state = HoverState.Idle
      if (!this.isFocused) {
        this.onOut(e)
      }
    }, this.config.timeout)
  }

  private readonly handleFocus = (e: FocusEvent): void => {
    if (this.isMouseOver) return
    this.isFocused = true
    this.onOver(e)
  }

  private readonly handleBlur = (e: FocusEvent): void => {
    if (!this.isFocused) return

    // A blur always clears focus state, even while the mouse is still over the
    // element. Resetting unconditionally prevents `isFocused` from sticking at
    // `true` and permanently suppressing later hover callbacks.
    this.isFocused = false

    // While hovered, the mouse owns the hover-out lifecycle, so skip onOut here
    // to avoid a double-fire — handleMouseOut will emit it once the cursor leaves.
    if (this.isMouseOver) return
    this.onOut(e)
  }

  private bindMouseListeners(): void {
    this.el.addEventListener('mouseover', this.handleMouseOver, PASSIVE)
    this.el.addEventListener('mouseout', this.handleMouseOut, PASSIVE)
  }

  private unbindMouseListeners(): void {
    this.el.removeEventListener('mouseover', this.handleMouseOver)
    this.el.removeEventListener('mouseout', this.handleMouseOut)
  }

  private bindFocusListeners(): void {
    this.el.addEventListener('focus', this.handleFocus, PASSIVE)
    this.el.addEventListener('blur', this.handleBlur, PASSIVE)
  }

  private unbindFocusListeners(): void {
    this.el.removeEventListener('focus', this.handleFocus)
    this.el.removeEventListener('blur', this.handleBlur)
  }

  updateOptions(opts: Partial<HoverIntentOptions>): void {
    const focusChanged = opts.handleFocus !== undefined && opts.handleFocus !== this.config.handleFocus

    this.config = { ...this.config, ...opts }

    if (focusChanged) {
      if (this.config.handleFocus) {
        this.bindFocusListeners()
      } else {
        this.unbindFocusListeners()

        // Once the blur listener is gone, handleBlur can no longer clear
        // `isFocused`. Reset it here so a lingering `true` doesn't permanently
        // suppress every mouse onOver/onOut callback (both gated behind
        // `!isFocused`).
        this.isFocused = false
      }
    }
  }

  destroy(): void {
    this.clearTimer()

    // `mousemove` is bound on demand during the detection window. If destroy()
    // runs while the cursor is still over the element (e.g. an SPA route change
    // mid-hover), it would otherwise stay attached and pin this instance in
    // memory. Remove it explicitly alongside the static listeners.
    this.el.removeEventListener('mousemove', this.handleMouseMove)
    this.unbindMouseListeners()
    this.unbindFocusListeners()
  }
}

/**
 * Detect intentional mouse hovers on a DOM element.
 *
 * It works by tracking the cursor position over a short interval and only
 * firing the `onOver` callback when the cursor has slowed down enough
 * (i.e., moved less than `sensitivity` pixels between checks).
 */
export function hoverIntent(el: HTMLElement, onOver: HoverIntentCallback, onOut: HoverIntentCallback): HoverIntentHandle {
  const tracker = new HoverIntentTracker(el, onOver, onOut)

  const handle: HoverIntentHandle = {
    options(opts: Partial<HoverIntentOptions>): HoverIntentHandle {
      tracker.updateOptions(opts)
      return handle
    },

    remove(): void {
      tracker.destroy()
    },
  }

  return handle
}
